import { Observable } from 'rxjs';
import { Company, CompanyPage, NewCompany } from '../models/company.model';
import { Job, JobPage, NewJobRef, RecruitEvent } from '../models/job.model';
import { SmartJd, SmartJdDocument, SmartJdLock } from '../models/smart-jd.model';
import { JobMatches } from '../models/match-result.model';
import { NewResume, ResumeUpload } from '../models/resume.model';
import { editLockMessage } from '../util/edit-lock';

/**
 * Everything the UI needs from the backend. Pages depend on this abstract class only.
 * MockRecruitApi (in-memory) and HttpRecruitApi (REST) both implement it; app.config picks one.
 */
/** Recruit Service rejects a company whose name is already taken. */
export class DuplicateCompanyName extends Error {
  constructor() {
    super('A company with this name already exists.');
  }
}

/** Refusals of a write to a Smart JD, by the slug Recruit Service names. Both implementations use these. */
export const WRITE_REJECTION: Record<string, string> = {
  'smart-jd-lock-required': 'The edit lock was lost. Try again.',
  'smart-jd-no-draft': 'There is no draft to publish.',
  'invalid-smart-jd': 'Recruit Service did not recognise that as a Smart JD.',
  'smart-jd-generation-failed': 'Generation failed for this Smart JD, so it cannot be published.',
};

/** Refusals of a copy, by the slug Recruit Service names. Both implementations use these. */
export const COPY_REJECTION: Record<string, string> = {
  'smart-jd-not-published': 'Publish this Smart JD before copying it.',
  'job-not-found': 'No job with this short code.',
};

/** When a copy fails for a reason Recruit Service does not name. */
export const COPY_FAILED = 'The copy did not go through.';

/** Refusals of a delete, by the slug Recruit Service names. Both implementations use these. */
export const DELETE_REJECTION: Record<string, string> = {
  'job-not-found': 'No job with this short code.',
};

/** When a delete fails for a reason Recruit Service does not name. */
export const DELETE_FAILED = 'The job was not deleted.';

export const SMART_JD_GONE = 'This job no longer has a Smart JD.';

/** The caller holds no edit lock. Distinct from SmartJdLocked: nobody else need hold it. */
export class SmartJdLockRequired extends Error {
  constructor() {
    super(WRITE_REJECTION['smart-jd-lock-required']);
  }
}

/** Another recruiter holds the edit lock. Never inferred from the session: the wire names them. */
export class SmartJdLocked extends Error {
  constructor(readonly lock: SmartJdLock) {
    super(editLockMessage(lock));
  }
}

export abstract class RecruitApi {
  /** One page of companies, 1-based. */
  abstract listCompanies(page: number): Observable<CompanyPage>;
  abstract getCompany(id: string): Observable<Company>;
  abstract createCompany(input: NewCompany): Observable<Company>;

  /** One page of a company's jobs, 1-based. */
  abstract listJobs(companyId: string, page: number): Observable<JobPage>;
  /** Opens a job. The Smart JD is generated after this returns — poll getSmartJd for it. */
  abstract uploadJd(companyId: string, file: File): Observable<NewJobRef>;
  /**
   * Opens a new job in the same company from this job's latest Published Smart JD, published
   * again as version 1. Nothing is generated, so the copy comes back READY and needs no event.
   */
  abstract copyJob(jobShortCode: string): Observable<Job>;
  /**
   * Soft-deletes the job: it leaves the company's list and every job endpoint reports it as not
   * found from then on. Takes no edit lock — the contract requires none — and cannot be undone.
   */
  abstract deleteJob(jobShortCode: string): Observable<void>;

  /** Null while Recruit Service has not generated it yet — an expected state, not an error. */
  abstract getSmartJd(jobShortCode: string): Observable<SmartJdDocument | null>;

  /**
   * Uploads resumes, one request per file, each carrying the candidate's name. Reports what
   * happened to each upload and nothing about scoring: the response carries no candidate
   * identifier, so a batch can never be followed to completion. See backend gap 9.
   */
  abstract uploadResumes(jobShortCode: string, resumes: NewResume[]): Observable<ResumeUpload[]>;
  /**
   * Every candidate on the job, split three ways: scored, still being scored, and those whose wait
   * ended without a result. The three are disjoint — a re-score of someone already scored stays
   * among the scored, carrying `pending`.
   */
  abstract listMatches(jobShortCode: string): Observable<JobMatches>;

  /** Takes or extends the 15-minute edit lock. Every write to a Smart JD needs it, publish included. */
  abstract lockSmartJd(jobShortCode: string): Observable<SmartJdLock>;
  /** Saves the whole document as the job's Draft. Needs the edit lock, and extends it. */
  abstract saveSmartJd(jobShortCode: string, smartJd: SmartJd): Observable<SmartJdDocument>;
  /** Promotes the Draft to the next Published version, and releases the lock. */
  abstract publishSmartJd(jobShortCode: string): Observable<SmartJdDocument>;
  /** Releasing a lock the caller does not hold changes nothing, so this is safe to fire blind. */
  abstract releaseSmartJdLock(jobShortCode: string): Observable<void>;

  /** The recruiter's own event stream, every frame tagged by the event name the wire sends.
   *  Reconnecting, sharing and filtering belong to RecruitEventsService, so an implementation
   *  here only connects and emits. */
  abstract events(): Observable<RecruitEvent>;
}
